import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { Check, ChevronsUpDown, Pencil, Trash2, Upload, Download, IndianRupee, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const CLIENT_OPTIONS = [
  { value: 'pro-hub', label: 'Pro-Hub' },
  { value: 'container-bau', label: 'Container Bau' },
];
const PROJECT_TYPES = [
  { value: 'lifter', label: 'Lifter' },
  { value: 'container', label: 'Container' },
  { value: 'turned parts', label: 'Turned Parts' },
  { value: 'service', label: 'Service' },
];

const emptyForm = {
  client: '', westaw_project_number: '', client_project_number: '',
  delivery_date: '', project_type: '',
  laser_parts_cost: '', structure_parts_cost: '', turned_parts_cost: '',
  milling_cost: '', assembly_cost: '',
  dispatch_invoice_number: '', dispatch_date: '', invoiced_value: '', ship_name: '', booking_number: '',
  conversion_rate: '',
};

const num = (v) => parseFloat(v) || 0;
const fmtINR = (v) => '₹' + Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtEUR = (v) => '€' + Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '-');

function ProjectCommercials() {
  const [projects, setProjects] = useState([]);
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [projOpen, setProjOpen] = useState(false);
  const [rateNote, setRateNote] = useState('');
  const fileInputRef = useRef(null);
  const [pendingUpload, setPendingUpload] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    fetchProjects();
    fetchRecords();
    fetchRate();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/projects`, authHeaders());
      setProjects(res.data);
    } catch (e) { /* ignore */ }
  };

  const fetchRecords = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/project-commercials`, authHeaders());
      setRecords(res.data);
    } catch (e) {
      toast.error('Failed to load project commercials');
    }
  };

  const fetchRate = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/fx/eur-inr`, authHeaders());
      const adjusted = Math.round((res.data.rate - 4) * 100) / 100;
      setForm((f) => ({ ...f, conversion_rate: f.conversion_rate || String(adjusted) }));
      setRateNote(`Live EUR→INR on ${res.data.date}: ${res.data.rate} − 4 = ${adjusted} (editable)`);
    } catch (e) {
      setRateNote('Could not fetch live EUR→INR rate. Please enter the rate manually.');
    }
  };

  const isTurnedParts = form.project_type === 'turned parts';

  const liveTotals = () => {
    const total = isTurnedParts
      ? num(form.turned_parts_cost)
      : num(form.laser_parts_cost) + num(form.structure_parts_cost) + num(form.turned_parts_cost) + num(form.milling_cost) + num(form.assembly_cost);
    const rate = num(form.conversion_rate);
    return { total, eur: rate ? total / rate : 0 };
  };

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const resetForm = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    fetchRate();
  };

  const startEdit = (rec) => {
    setEditingId(rec.id);
    setForm({
      client: rec.client || '',
      westaw_project_number: rec.westaw_project_number || '',
      client_project_number: rec.client_project_number || '',
      delivery_date: rec.delivery_date ? String(rec.delivery_date).split('T')[0] : '',
      project_type: rec.project_type || '',
      laser_parts_cost: String(rec.laser_parts_cost ?? ''),
      structure_parts_cost: String(rec.structure_parts_cost ?? ''),
      turned_parts_cost: String(rec.turned_parts_cost ?? ''),
      milling_cost: String(rec.milling_cost ?? ''),
      assembly_cost: String(rec.assembly_cost ?? ''),
      dispatch_invoice_number: rec.dispatch_invoice_number || '',
      dispatch_date: rec.dispatch_date ? String(rec.dispatch_date).split('T')[0] : '',
      invoiced_value: String(rec.invoiced_value ?? ''),
      ship_name: rec.ship_name || '',
      booking_number: rec.booking_number || '',
      conversion_rate: String(rec.conversion_rate ?? ''),
    });
    if (formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client || !form.westaw_project_number || !form.delivery_date || !form.project_type) {
      toast.error('Please fill Client, WESTAW Project, Delivery Date and Project Type');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client: form.client,
        westaw_project_number: form.westaw_project_number,
        client_project_number: form.client_project_number,
        delivery_date: `${form.delivery_date}T00:00:00`,
        project_type: form.project_type,
        laser_parts_cost: isTurnedParts ? 0 : num(form.laser_parts_cost),
        structure_parts_cost: isTurnedParts ? 0 : num(form.structure_parts_cost),
        turned_parts_cost: num(form.turned_parts_cost),
        milling_cost: isTurnedParts ? 0 : num(form.milling_cost),
        assembly_cost: isTurnedParts ? 0 : num(form.assembly_cost),
        dispatch_invoice_number: form.dispatch_invoice_number,
        dispatch_date: form.dispatch_date ? `${form.dispatch_date}T00:00:00` : null,
        invoiced_value: num(form.invoiced_value),
        ship_name: form.ship_name,
        booking_number: form.booking_number,
        conversion_rate: num(form.conversion_rate),
      };
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/project-commercials/${editingId}`, payload, authHeaders());
        toast.success('Project updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/project-commercials`, payload, authHeaders());
        toast.success('Project added');
      }
      resetForm();
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/project-commercials/${id}`, authHeaders());
      toast.success('Project deleted');
      if (editingId === id) resetForm();
      fetchRecords();
    } catch (e) {
      toast.error('Failed to delete project');
    }
  };

  const triggerUpload = (recordId, kind) => {
    setPendingUpload({ recordId, kind });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingUpload) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(
        `${BACKEND_URL}/api/project-commercials/${pendingUpload.recordId}/invoice/${pendingUpload.kind}`,
        fd,
        { headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`${pendingUpload.kind === 'pi' ? 'PI' : 'Invoice'} uploaded`);
      fetchRecords();
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setPendingUpload(null);
    }
  };

  const downloadInvoice = async (recordId, kind, filename) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/project-commercials/${recordId}/invoice/${kind}`, {
        ...authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `${kind}_invoice`);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const getProjectDisplay = () => {
    const p = projects.find((x) => x.westaw_project_number === form.westaw_project_number);
    return p ? `${p.westaw_project_number} - ${p.client_name}` : 'Search and select a WESTAW project...';
  };

  // group records by month-year of delivery_date
  const groupByMonth = () => {
    const groups = {};
    records.forEach((r) => {
      const d = new Date(r.delivery_date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      if (!groups[key]) groups[key] = { label, rows: [] };
      groups[key].rows.push(r);
    });
    return Object.keys(groups).sort().reverse().map((k) => groups[k]);
  };

  const totals = liveTotals();
  const costField = (label, key, disabled) => (
    <div className="space-y-1">
      <Label className="text-xs">{label} (₹)</Label>
      <Input
        type="number" min="0" value={form[key]}
        onChange={(e) => setField(key, e.target.value)}
        disabled={disabled}
        className={disabled ? 'bg-slate-100' : ''}
        data-testid={`pc-${key}`}
      />
    </div>
  );

  const uploadBtn = (rec, kind, label) => {
    const inv = kind === 'pi' ? rec.pi_file : rec.invoice_file;
    return (
      <Button size="sm" variant="outline" className="h-7 px-2 whitespace-nowrap" onClick={() => triggerUpload(rec.id, kind)} data-testid={`upload-${kind}-${rec.id}`} title={inv ? inv.original_filename : `Upload ${label}`}>
        <Upload className="w-3 h-3 mr-1" />{label}{inv ? ' ✓' : ''}
      </Button>
    );
  };

  const downloadReport = async (rec) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/project-commercials/${rec.id}/report`, { ...authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project_${rec.westaw_project_number}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
    } catch (e) {
      toast.error('Report download failed');
    }
  };

  const downloadAll = async (rec) => {
    await downloadReport(rec);
    await new Promise((r) => setTimeout(r, 500));
    if (rec.pi_file) { await downloadInvoice(rec.id, 'pi', rec.pi_file.original_filename); await new Promise((r) => setTimeout(r, 500)); }
    if (rec.invoice_file) { await downloadInvoice(rec.id, 'invoice', rec.invoice_file.original_filename); }
    toast.success('Download started (project report + uploaded documents)');
  };

  const documentsCell = (rec) => (
    <div className="flex items-center gap-1.5 min-w-[280px]">
      {uploadBtn(rec, 'pi', 'PI')}
      {uploadBtn(rec, 'invoice', 'Invoice')}
      <Button size="sm" variant="outline" className="h-7 px-2 whitespace-nowrap" onClick={() => downloadAll(rec)} data-testid={`download-both-${rec.id}`}>
        <Download className="w-3 h-3 mr-1" />Download
      </Button>
    </div>
  );

  const monthGroups = groupByMonth();

  return (
    <div className="space-y-6">
      <input type="file" ref={fileInputRef} onChange={onFileSelected} className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="pc-file-input" />

      <div ref={formRef}>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="project-commercials-title">Project Commercials</h1>
        <p className="text-slate-600 mt-2">Project &amp; financial management — costing, dispatch and invoices</p>
      </div>

      {/* Entry form */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-xl font-semibold">{editingId ? 'Edit Project' : 'New Project'}</CardTitle>
          <CardDescription>Enter project commercial &amp; financial details</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>Client</Label>
                <Select value={form.client} onValueChange={(v) => setField('client', v)}>
                  <SelectTrigger data-testid="pc-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {CLIENT_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>WESTAW Project</Label>
                <Popover open={projOpen} onOpenChange={setProjOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="pc-project-select">
                      <span className={form.westaw_project_number ? 'text-slate-900' : 'text-slate-500'}>{getProjectDisplay()}</span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search projects..." />
                      <CommandList>
                        <CommandEmpty>No project found.</CommandEmpty>
                        <CommandGroup>
                          {projects.map((p) => (
                            <CommandItem key={p.id} value={`${p.westaw_project_number} ${p.client_name} ${p.client_project_number}`}
                              onSelect={() => { setField('westaw_project_number', p.westaw_project_number); setProjOpen(false); }}>
                              <Check className={cn('mr-2 h-4 w-4', form.westaw_project_number === p.westaw_project_number ? 'opacity-100' : 'opacity-0')} />
                              <div className="flex flex-col">
                                <span className="font-medium">{p.westaw_project_number}</span>
                                <span className="text-xs text-slate-500">{p.client_name} • {p.client_project_number}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <Label>Client Project Number</Label>
                <Input value={form.client_project_number} onChange={(e) => setField('client_project_number', e.target.value)} placeholder="Type client project number" data-testid="pc-client-project-number" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>Delivery Date</Label>
                <Input type="date" value={form.delivery_date} onChange={(e) => setField('delivery_date', e.target.value)} data-testid="pc-delivery-date" />
              </div>
              <div className="space-y-1">
                <Label>Project Type</Label>
                <Select value={form.project_type} onValueChange={(v) => setField('project_type', v)}>
                  <SelectTrigger data-testid="pc-project-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {PROJECT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Conversion Rate (EUR→INR)</Label>
                <Input type="number" step="0.01" value={form.conversion_rate} onChange={(e) => setField('conversion_rate', e.target.value)} data-testid="pc-conversion-rate" />
              </div>
            </div>
            {rateNote && <p className="text-xs text-slate-500 -mt-2">{rateNote}</p>}

            <div>
              <Label className="text-sm font-semibold text-slate-700">Costing {isTurnedParts && <span className="text-amber-600 font-normal">(only Turned Parts applies for this type)</span>}</Label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2">
                {costField('Laser Parts', 'laser_parts_cost', isTurnedParts)}
                {costField('Structure Parts', 'structure_parts_cost', isTurnedParts)}
                {costField('Turned Parts', 'turned_parts_cost', false)}
                {costField('Milling', 'milling_cost', isTurnedParts)}
                {costField('Assembly', 'assembly_cost', isTurnedParts)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="space-y-1">
                <Label>Dispatch Invoice Number</Label>
                <Input value={form.dispatch_invoice_number} onChange={(e) => setField('dispatch_invoice_number', e.target.value)} data-testid="pc-dispatch-invoice" />
              </div>
              <div className="space-y-1">
                <Label>Dispatch Date</Label>
                <Input type="date" value={form.dispatch_date} onChange={(e) => setField('dispatch_date', e.target.value)} data-testid="pc-dispatch-date" />
              </div>
              <div className="space-y-1">
                <Label>Invoiced Value (₹)</Label>
                <Input type="number" min="0" value={form.invoiced_value} onChange={(e) => setField('invoiced_value', e.target.value)} data-testid="pc-invoiced-value" />
              </div>
              <div className="space-y-1">
                <Label>Ship Name</Label>
                <Input value={form.ship_name} onChange={(e) => setField('ship_name', e.target.value)} data-testid="pc-ship-name" />
              </div>
              <div className="space-y-1">
                <Label>Booking Number</Label>
                <Input value={form.booking_number} onChange={(e) => setField('booking_number', e.target.value)} data-testid="pc-booking-number" />
              </div>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 font-semibold text-slate-700"><IndianRupee className="w-4 h-4" />Total: {fmtINR(totals.total)}</span>
                <span className="font-semibold text-emerald-700">{fmtEUR(totals.eur)}</span>
              </div>
              <div className="flex gap-2">
                {editingId && (
                  <Button type="button" variant="outline" onClick={resetForm} data-testid="pc-cancel-edit">
                    <X className="w-4 h-4 mr-1" />Cancel
                  </Button>
                )}
                <Button type="submit" disabled={saving} style={{ backgroundColor: '#008080' }} data-testid="pc-submit">
                  {saving ? 'Saving...' : editingId ? 'Update Project' : 'Add Project'}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Records grouped by month */}
      <div className="space-y-8">
        {records.length === 0 && (
          <Card className="bg-white border-slate-200"><CardContent className="p-8 text-center text-slate-500">No projects added yet. Use the form above to add your first project.</CardContent></Card>
        )}
        {monthGroups.map((group) => (
          <div key={group.label} data-testid={`month-group-${group.label}`}>
            <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="inline-block w-1.5 h-5 rounded-full" style={{ backgroundColor: '#008080' }} />
              {group.label}
              <span className="text-sm font-normal text-slate-400">({group.rows.length})</span>
            </h3>
            <Card className="bg-white border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1100px] text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-left">
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-2 whitespace-nowrap">WESTAW Project</th>
                      <th className="px-3 py-2 whitespace-nowrap">Client Project</th>
                      <th className="px-3 py-2 whitespace-nowrap">Client</th>
                      <th className="px-3 py-2 whitespace-nowrap">Type</th>
                      <th className="px-3 py-2 whitespace-nowrap">Delivery</th>
                      <th className="px-3 py-2 whitespace-nowrap">Dispatch Inv</th>
                      <th className="px-3 py-2 whitespace-nowrap">Dispatch Date</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">Invoiced Value</th>
                      <th className="px-3 py-2 whitespace-nowrap">Ship</th>
                      <th className="px-3 py-2 whitespace-nowrap">Booking</th>
                      <th className="px-3 py-2 whitespace-nowrap">Documents (PI / Invoice)</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap bg-teal-50">Total Est. ₹</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap bg-teal-50">Total Est. €</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((rec) => (
                      <tr key={rec.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pc-row-${rec.id}`}>
                        <td className="px-3 py-2 whitespace-nowrap font-medium">{rec.westaw_project_number}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{rec.client_project_number || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{CLIENT_OPTIONS.find((c) => c.value === rec.client)?.label || rec.client}</td>
                        <td className="px-3 py-2 whitespace-nowrap capitalize">{rec.project_type}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(rec.delivery_date)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{rec.dispatch_invoice_number || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(rec.dispatch_date)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">{rec.invoiced_value ? fmtINR(rec.invoiced_value) : '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{rec.ship_name || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{rec.booking_number || '-'}</td>
                        <td className="px-3 py-2">
                          {documentsCell(rec)}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap font-semibold bg-teal-50">{fmtINR(rec.total_cost_inr)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-emerald-700 bg-teal-50">{fmtEUR(rec.total_cost_eur)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => startEdit(rec)} data-testid={`pc-edit-${rec.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:text-red-700" data-testid={`pc-delete-${rec.id}`}><Trash2 className="w-4 h-4" /></Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete this project?</AlertDialogTitle>
                                  <AlertDialogDescription>This will permanently remove the project commercial record for {rec.westaw_project_number}.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel data-testid="pc-cancel-delete">Cancel</AlertDialogCancel>
                                  <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(rec.id)} data-testid="pc-confirm-delete">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProjectCommercials;
